import { activeSiteCountry } from "@config/site/site-config";
import { dateSelectorEnabledDeliveryMethods } from "@lib/constants/dateSelectorDeliveryMethods";
import {
    formSupportsBillingAdditionalAddressInfo,
    formSupportsShippingAdditionalAddressInfo,
} from "@features/checkout/components/delivery-step/FormsConfig";
import type { Cart, CustomFieldInput, MyCartUpdateAction } from "@graphql/generated/types";

const ADDRESS_CUSTOM_FIELDS_TYPE_KEY = "address-customfields-type";

// Copies of what the checkout add* operations do to the cart, but returning the CommerceTools
// update action(s) instead of mutating. This lets the whole delivery step be committed in a
// single updateMyCart call. Address inputs are built from an explicit field whitelist (never
// spreading the raw cart Address, which carries __typename/id that CT rejects).

export const buildDeliveryDateAndTimeSlotActions = (
    cart: Cart,
    values: { date: unknown; timeslot: unknown }
): MyCartUpdateAction[] => {
    const existingCustomField = (name: string) =>
        cart?.custom?.customFieldsRaw?.find((item) => item.name === name)?.value;
    const dateExists = existingCustomField("order_CF_DeliveryDate");
    const timeslotExists = existingCustomField("order_CF_DeliveryTimeSlot");

    if ((!dateExists && !values.date) || (!timeslotExists && !values.timeslot)) {
        return [];
    }
    const isActiveCountryWithDateSelector = activeSiteCountry in dateSelectorEnabledDeliveryMethods;
    if (!values.date && !values.timeslot && !isActiveCountryWithDateSelector) {
        return [];
    }
    return [
        { setCustomField: { name: "order_CF_DeliveryDate", value: JSON.stringify(values.date) } },
        {
            setCustomField: {
                name: "order_CF_DeliveryTimeSlot",
                value: JSON.stringify(values.timeslot),
            },
        },
    ];
};

export const buildShippingAddressAction = (
    cartShippingAddress: Cart["shippingAddress"],
    payload: Record<string, unknown>,
    customFields: CustomFieldInput[]
): MyCartUpdateAction => {
    const merged = { ...cartShippingAddress, ...payload } as unknown as Record<
        string,
        string | undefined
    >;
    const additionalAddressInfo = formSupportsShippingAdditionalAddressInfo(merged.country ?? "")
        ? ((payload.additionalAddressInfo as string | undefined) ?? undefined)
        : undefined;
    return {
        setShippingAddress: {
            address: {
                country: merged.country,
                firstName: merged.firstName,
                lastName: merged.lastName,
                streetName: merged.streetName,
                additionalAddressInfo,
                postalCode: merged.postalCode,
                city: merged.city,
                phone: merged.phone,
                email: merged.email,
                region: merged.region,
                company: merged.company,
                // Payload only, like additionalAddressInfo above: a salutation belongs to the
                // write that sets it, so leaving a pick-up method clears it rather than inheriting.
                salutation: payload.salutation as string | undefined,
                custom: { fields: customFields, typeKey: ADDRESS_CUSTOM_FIELDS_TYPE_KEY },
            },
        },
    };
};

export const buildBillingAddressAction = (
    cartBillingAddress: Cart["billingAddress"],
    payload: Record<string, unknown>,
    customFields: CustomFieldInput[]
): MyCartUpdateAction => {
    const merged = { ...cartBillingAddress, ...payload } as unknown as Record<
        string,
        string | undefined
    >;
    const additionalAddressInfo = formSupportsBillingAdditionalAddressInfo(merged.country ?? "")
        ? ((payload.additionalAddressInfo as string | undefined) ?? undefined)
        : undefined;
    return {
        setBillingAddress: {
            address: {
                country: merged.country,
                firstName: merged.firstName,
                lastName: merged.lastName,
                streetName: merged.streetName,
                additionalAddressInfo,
                postalCode: merged.postalCode,
                phone: merged.phone,
                city: merged.city,
                email: merged.email,
                region: merged.region,
                company: merged.company,
                custom: { fields: customFields, typeKey: ADDRESS_CUSTOM_FIELDS_TYPE_KEY },
            },
        },
    };
};

export const buildShippingMethodAction = (shippingMethodKey: string): MyCartUpdateAction => ({
    setShippingMethod: {
        shippingMethod: { typeId: "shipping-method", key: shippingMethodKey },
    },
});
